import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    PeriodicProcessLogsFieldsWithDefaults,
    PeriodicProcessLogsSchemaTypes,
} from "../../types/periodicProcessLogs.d.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "PeriodicProcessLogs" as const;

type RecordStatus = "success" | "error" | "warning" | "not_run" | "not_set";

const recordStatusSchema = z.enum(["success", "error", "warning", "not_run", "not_set"]);

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<PeriodicProcessLogs>>(...addSchemaKeyHook(SCHEMA_KEY))
export class PeriodicProcessLogs extends SchemaBase<PeriodicProcessLogs, PeriodicProcessLogsFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => [String], required: true, default: [] }, PropType.ARRAY)
    public logs: string[] = [];

    @validate({ zodValidator: recordStatusSchema.optional() })
    @prop({ type: () => String, required: true, default: "not_set" })
    public status: RecordStatus = "not_set";

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: true, default: -1 })
    public completionPercentage: number = -1;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public error?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public processName?: string;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public taskCompletionTime?: number;

    static getters = {} as const satisfies Record<string, (self: PeriodicProcessLogs) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<PeriodicProcessLogsSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<PeriodicProcessLogs, PeriodicProcessLogsFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

PeriodicProcessLogs.dtoValidators = getZodSchemaFromClass(
    PeriodicProcessLogs,
) as unknown as DtoValidatorTypes<PeriodicProcessLogsSchemaTypes>;
